import React, { useState, useEffect, useRef } from 'react';
import { AutoCompLoc } from './AutoCompLoc';

/* ═══════════════════════════════════════════════════
   SearchFilterBar - pure Tailwind segmented filter.
   Shared between Home and SearchResults.
   ═══════════════════════════════════════════════════ */

const segmentLabel = 'text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary leading-none mb-1.5';
const segmentValue = 'truncate text-[14.5px] font-medium text-fg-primary leading-none';
const segmentPlaceholder = 'truncate text-[14.5px] font-medium text-fg-tertiary leading-none';
const insetSegment = `
  group relative flex h-[72px] w-full items-center gap-3 rounded-xl px-4
  transition-[background-color] duration-200 ease-out-soft
  hover:bg-amber/5
`;
const insetActive = 'bg-amber/8';
const insetError = 'bg-red-400/10';

function SegDivider() {
  return <div aria-hidden className="hidden md:block w-px self-stretch my-3 bg-border-divider/50" />;
}

function SelectField({ label, value, onChange, options, required, error, icon, placeholder = 'Select...' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const selected = options.find((o) => o.value === value);
  const isPlaceholder = !selected || selected.value === '0';
  const display = !isPlaceholder ? selected.label : (selected ? selected.label : placeholder);

  const cls = `${insetSegment} ${error ? insetError : ''} ${open ? insetActive : ''} cursor-pointer text-left`;

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cls}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon && (
          <span className={`shrink-0 transition-colors duration-200 ease-out-soft ${open ? 'text-amber' : 'text-fg-tertiary'}`}>
            {icon}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={segmentLabel}>
            {label}
            {required && <span className="ml-1 text-amber">*</span>}
          </span>
          <span className={isPlaceholder ? segmentPlaceholder : segmentValue}>{display}</span>
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ease-out-soft ${open ? 'rotate-180 text-amber' : 'text-fg-tertiary'}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="
            absolute left-0 right-0 top-full z-50 mt-2
            max-h-80 overflow-auto rounded-xl
            border border-amber/30 bg-surface-elevated
            shadow-dialog
            animate-[fadeIn_140ms_ease-out]
            py-1
          "
        >
          {options.map((opt) => {
            const isPlace = opt.value === '0';
            const isSel = opt.value === value;
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`
                  flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left text-[13.5px]
                  transition-[background-color,color] duration-150 ease-out-soft
                  ${isSel
                    ? 'bg-amber/14 text-amber font-semibold'
                    : isPlace
                      ? 'text-fg-muted hover:bg-amber/8 hover:text-fg-secondary'
                      : 'text-fg-secondary hover:bg-amber/8 hover:text-fg-primary'
                  }
                `}
              >
                <span className="truncate">{opt.label}</span>
                {isSel && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0 text-amber">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TextInput({ label, icon, value, onChange, placeholder, required }) {
  const [focused, setFocused] = useState(false);
  const cls = `${insetSegment} ${focused ? insetActive : ''} cursor-text`;
  return (
    <label className={cls}>
      {icon && (
        <span className={`shrink-0 transition-colors duration-200 ease-out-soft ${focused ? 'text-amber' : 'text-fg-tertiary'}`}>
          {icon}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={segmentLabel}>
          {label}
          {required && <span className="ml-1 text-amber">*</span>}
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className="w-full bg-transparent text-[14.5px] font-medium text-fg-primary leading-none placeholder:text-fg-tertiary outline-none border-0 p-0"
        />
      </span>
    </label>
  );
}

const IconTag = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d="M20.59 13.41 13 21 3 11V3h8z" />
    <circle cx="7.5" cy="7.5" r="1.2" />
  </svg>
);
const IconLayer = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d="m12 3 9 5-9 5-9-5 9-5z" />
    <path d="m3 13 9 5 9-5" />
    <path d="m3 18 9 5 9-5" />
  </svg>
);
const IconSearchSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/* ═══════════════════ SearchFilterBar ═══════════════════ */

export const SearchFilterBar = ({
  loc, setLoc,
  cat, setCat,
  subCat, setSubcat,
  search, setSearch,
  cats = [],
  subcatOptions = [],
  error = 0,
  onSubmit,
  buttonLabel = 'Search',
  className = '',
  showHint = true,
}) => {
  const canSearch = Boolean(loc) && cat !== '0';

  return (
    <div className={`relative z-30 mx-auto w-full max-w-[1120px] ${className}`}>
      <form
        onSubmit={onSubmit}
        noValidate
        className="
          relative z-30 flex flex-col md:flex-row md:items-stretch
          rounded-2xl
          bg-[linear-gradient(180deg,rgba(26,26,29,0.94)_0%,rgba(18,18,20,0.94)_100%)]
          backdrop-blur-2xl backdrop-saturate-150
          border border-border-subtle
          shadow-[0_24px_72px_-24px_rgba(0,0,0,0.6),0_0_72px_-12px_rgba(251,191,36,0.18)]
          p-1.5
          gap-1.5 md:gap-0
          transition-[border-color,box-shadow] duration-300 ease-out-soft
          focus-within:border-amber/60
          focus-within:shadow-[0_0_0_1px_rgba(251,191,36,0.45),0_0_88px_-8px_rgba(251,191,36,0.45),0_24px_72px_-24px_rgba(0,0,0,0.65)]
        "
      >
        <div aria-hidden className="pointer-events-none absolute inset-x-12 -top-px h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

        {/* Location */}
        <div className="md:flex-1 md:min-w-0">
          <AutoCompLoc loc={loc} setloc={setLoc} error={error === 1} label="Location" />
        </div>

        <SegDivider />

        {/* Category */}
        <div className="md:flex-1 md:min-w-0">
          <SelectField
            label="Category"
            required
            value={cat}
            onChange={(v) => { setCat(v); setSubcat('0'); }}
            error={error === 2}
            icon={IconTag}
            placeholder="Select category"
            options={[
              { value: '0', label: 'Select category' },
              ...cats.map((c) => ({ value: c.category_id, label: c.category_name })),
            ]}
          />
        </div>

        {cat !== '0' && subcatOptions.length > 0 && (
          <>
            <SegDivider />
            <div className="md:flex-1 md:min-w-0">
              <SelectField
                label="Sub-category"
                value={subCat}
                onChange={setSubcat}
                icon={IconLayer}
                placeholder="Any sub-category"
                options={[
                  { value: '0', label: 'Any sub-category' },
                  ...subcatOptions.map((sc) => ({ value: sc.sub_category_id, label: sc.sub_category_name })),
                ]}
              />
            </div>
          </>
        )}

        <SegDivider />

        {/* Keywords */}
        <div className="md:flex-1 md:min-w-0">
          <TextInput
            label="Keywords"
            icon={IconSearchSvg}
            placeholder="Optional"
            value={search}
            onChange={setSearch}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSearch}
          aria-disabled={!canSearch}
          className={`
            group relative inline-flex h-[72px] w-full md:w-auto md:ml-2 items-center justify-center gap-2
            rounded-xl px-8
            text-[13px] font-bold uppercase tracking-[0.12em]
            transition-all duration-200 ease-out-soft
            ${canSearch
              ? 'bg-amber text-ink hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_10px_28px_-8px_rgba(251,191,36,0.55)] active:scale-[0.98] cursor-pointer'
              : 'bg-amber/20 text-ink/40 cursor-not-allowed'
            }
          `}
        >
          <span>{buttonLabel}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className={`h-4 w-4 transition-transform duration-200 ease-out-soft ${canSearch ? 'group-hover:translate-x-1' : ''}`}
          >
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </button>
      </form>

      {showHint && !canSearch && (
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[11.5px] text-fg-tertiary">
          <span aria-hidden className="inline-block h-1 w-1 rounded-full bg-amber/70" />
          <span>
            <span className={loc ? 'text-fg-secondary' : 'text-amber'}>Location</span>
            {' and '}
            <span className={cat !== '0' ? 'text-fg-secondary' : 'text-amber'}>category</span>
            {' are required to search'}
          </span>
        </div>
      )}
    </div>
  );
};
